/**
 * useSkipToResults Hook
 *
 * Handles the "Skip to Results" fast-track experience for paid users.
 * Monitors API completion and triggers immediate navigation to results.
 *
 * PATTERN:
 * Unlike theatrical/VIP modes that wait for animation completion,
 * "Skip to Results" navigates AS SOON AS the API returns data.
 * This is the fastest path from submission to results.
 *
 * USAGE:
 * ```tsx
 * // In analyze/[id].tsx when mode is 'quick'
 * const { shouldShowLoader, handleNavigateToResults } = useSkipToResults({
 *   analysisId: params.id,
 *   isQuickMode: effectiveTheatricalVariant === 'quick',
 *   onNavigate: (analysisId) => {
 *     router.replace({
 *       pathname: '/results/[analysisId]',
 *       params: { analysisId, mode: 'quick' },
 *     });
 *   },
 * });
 *
 * if (isQuickMode && shouldShowLoader) {
 *   return <SkipToResultsLoader message={message} />;
 * }
 * ```
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAnalysisStore } from '../store/analysis';
import { captureEvent } from '../lib/analytics';

interface UseSkipToResultsOptions {
  /** Temporary analysis ID from submission */
  analysisId: string | undefined;
  /** Whether quick mode is active */
  isQuickMode: boolean;
  /** Navigation callback when API completes */
  onNavigate: (analysisId: string) => void;
  /** Optional: Track analytics events */
  trackingEnabled?: boolean;
}

interface UseSkipToResultsReturn {
  /** Whether to show the skip-to-results loader */
  shouldShowLoader: boolean;
  /** Analysis result data (null while pending) */
  analysisResult: unknown | null;
  /** Whether API call is still pending */
  isPending: boolean;
  /** Manual navigation trigger (if needed) */
  handleNavigateToResults: () => void;
}

/**
 * Hook for "Skip to Results" mode.
 *
 * Monitors the Zustand analysis store and navigates immediately
 * when the API response is available. No animation delay.
 */
export function useSkipToResults({
  analysisId,
  isQuickMode,
  onNavigate,
  trackingEnabled = true,
}: UseSkipToResultsOptions): UseSkipToResultsReturn {
  // Track if we've already navigated (prevent double navigation)
  const hasNavigatedRef = useRef(false);
  const navigationStartTimeRef = useRef<number | null>(null);

  // Get analysis from store
  const analysisItem = useAnalysisStore((state) =>
    analysisId ? state.getById(analysisId) : undefined
  );

  const isPending = analysisItem?.status === 'pending';
  const analysisResult = analysisItem?.result ?? null;

  // Track when we start waiting (for analytics)
  useEffect(() => {
    if (isQuickMode && isPending && !navigationStartTimeRef.current) {
      navigationStartTimeRef.current = Date.now();
    }
  }, [isQuickMode, isPending]);

  // Auto-navigate when API completes
  useEffect(() => {
    // Only act in quick mode
    if (!isQuickMode) return;

    // Need analysis ID
    if (!analysisId) return;

    // Already navigated
    if (hasNavigatedRef.current) return;

    // Need result data and not pending
    if (!analysisResult || isPending) return;

    // Get the actual analysis ID from the result
    const actualAnalysisId =
      (analysisResult as { id?: string })?.id ?? analysisId;

    // Track analytics
    if (trackingEnabled && navigationStartTimeRef.current) {
      const waitTimeMs = Date.now() - navigationStartTimeRef.current;
      captureEvent('skip_to_results_navigation', {
        temp_id: analysisId,
        analysis_id: actualAnalysisId,
        wait_time_ms: waitTimeMs,
        mode: 'quick',
      });
    }

    // Mark as navigated and trigger navigation
    hasNavigatedRef.current = true;
    onNavigate(actualAnalysisId);
  }, [
    isQuickMode,
    analysisId,
    analysisResult,
    isPending,
    onNavigate,
    trackingEnabled,
  ]);

  // Manual navigation trigger (if parent needs to control)
  const handleNavigateToResults = useCallback(() => {
    if (hasNavigatedRef.current) return;
    if (!analysisResult) return;

    const actualAnalysisId =
      (analysisResult as { id?: string })?.id ?? analysisId ?? '';

    hasNavigatedRef.current = true;
    onNavigate(actualAnalysisId);
  }, [analysisId, analysisResult, onNavigate]);

  // Reset navigation flag when analysis ID changes (new submission)
  useEffect(() => {
    hasNavigatedRef.current = false;
    navigationStartTimeRef.current = null;
  }, [analysisId]);

  return {
    shouldShowLoader: isQuickMode && isPending,
    analysisResult,
    isPending,
    handleNavigateToResults,
  };
}

export default useSkipToResults;
